import React, { useEffect, useMemo, useState } from "react";

import Footer from "./footer";
import StockDetails from "./stockDetails";
import { createEmptyStock, formatStockFromRaw, getChapterHighlightStyle } from "./stockUtils";
import {
    fetchJson,
    fetchStockDataWithFallback,
    getRecentDateStrings,
    fetchChapterHighlights,
    fetchLatestFilterHistory,
} from "./dataFetcher";
import "./body.css";
import "./fave.css";

const parseFaveDate = (dateStr) => {
    const parts = (dateStr || "").split("-");
    if (parts.length !== 3) {
        return null;
    }

    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const year = parseInt(parts[2], 10);

    if (Number.isNaN(day) || Number.isNaN(month) || Number.isNaN(year)) {
        return null;
    }

    const parsed = new Date(year, month, day);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatPrettyDate = (dateStr) => {
    const parsed = parseFaveDate(dateStr);
    if (!parsed) {
        return dateStr;
    }

    return parsed.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
    });
};

const getNextSundayReset = (now) => {
    const target = new Date(now);
    target.setUTCHours(8, 8, 0, 0); // 08:03 UTC + 5 min buffer for script to finish

    const day = now.getUTCDay(); // 0 = Sunday
    if (day !== 0) {
        target.setUTCDate(target.getUTCDate() + (7 - day));
    } else if (now >= target) {
        target.setUTCDate(target.getUTCDate() + 7);
    }

    return target;
};

function Fave() {
    let [time, setTime] = useState(Date.now());
    let [lastUpdate, setLastUpdate] = useState("July 19, 1999");
    const [fave, setFave] = useState(() => createEmptyStock());
    const [isReady, setIsReady] = useState(false);
    const [error, setError] = useState("");
    const [dataDate, setDataDate] = useState("");
    const [chapterHighlight, setChapterHighlight] = useState({
        ch14Success: false,
        ch15Success: false,
    });
    const [latestFilterEntry, setLatestFilterEntry] = useState(null);


    useEffect(() => {
        const interval = setInterval(() => setTime(Date.now()), 1000);
        return () => {
            clearInterval(interval);
        };
    }, []);

    useEffect(() => {
        window.scrollTo(0, 0);
        document.title = "Invest With Knowledge! - Current Fave";

        const favesUrl = "https://investingfunctionstorage.blob.core.windows.net/alphavantage/faves.json";

        const loadFave = async () => {
            try {
                const [favesData, stockPayload] = await Promise.all([
                    fetchJson(favesUrl),
                    fetchStockDataWithFallback(getRecentDateStrings(4)),
                ]);

                const resolvedStockDate = formatPrettyDate(stockPayload.dateStr);

                const faveEntries = Array.isArray(favesData["stockdata"])
                    ? favesData["stockdata"]
                    : [];
                const selected =
                    faveEntries.length > 0 ? faveEntries[faveEntries.length - 1] : null;
                const resolvedFaveDate =
                    selected && selected.date ? formatPrettyDate(selected.date) : "";
                setLastUpdate(resolvedFaveDate || resolvedStockDate);
                const faveTicker = selected ? (selected.ticker || "").toLowerCase() : "";

                const currentFave = stockPayload.combined.find(
                    (stock) => (stock.ticker || "").toLowerCase() === faveTicker
                );

                if (currentFave) {
                    setDataDate(formatPrettyDate(currentFave.dataDate || stockPayload.dateStr));
                    const highlightMap = await fetchChapterHighlights(stockPayload.dateStr, [
                        faveTicker,
                    ]);
                    setChapterHighlight(
                        highlightMap[faveTicker] || { ch14Success: false, ch15Success: false }
                    );

                    const latestFilterRecord = await fetchLatestFilterHistory(
                        stockPayload.dateStr,
                        faveTicker
                    );
                    setLatestFilterEntry(latestFilterRecord);
                    setFave(formatStockFromRaw(currentFave));
                    setIsReady(true);
                } else {
                    setError("Unable to load current fave details right now.");
                }
            } catch (fetchError) {
                console.error("There was a problem with the fetch operation:", fetchError);
                setError("Unable to load current fave right now.");
            }
        };

        loadFave();
    }, []);

    const now = new Date(time);
    const nextReset = getNextSundayReset(now);
    const secondsUntilReset = Math.max(
        0,
        Math.floor((nextReset.getTime() - now.getTime()) / 1000)
    );
    const daysleft = Math.floor(secondsUntilReset / (60 * 60 * 24));
    const hoursleft = Math.floor((secondsUntilReset % (60 * 60 * 24)) / (60 * 60));
    const minutesleft = Math.floor((secondsUntilReset % (60 * 60)) / 60);
    const secondsleft = Math.floor(secondsUntilReset % 60);

    const sectionClass = `stockdiv ${isReady ? "fade-in" : ""}`;
    const highlightStyle = useMemo(
        () => getChapterHighlightStyle(chapterHighlight),
        [chapterHighlight]
    );

    const extractStatus = (text = "") => {
        const normalized = (typeof text === "string" ? text : "").trim().toUpperCase();
        if (normalized.startsWith("SUCCESS")) return "SUCCESS";
        if (normalized.startsWith("FAIL")) return "FAIL";
        return "";
    };

    const extractReasons = (text = "") => {
        const value = typeof text === "string" ? text : "";
        const trimmed = value.trim();
        const normalized = trimmed.toUpperCase();

        if (normalized.startsWith("SUCCESS")) return [];
        if (normalized.startsWith("FAIL")) {
            const rest = trimmed.replace(/^FAIL(?:\s*:)?\s*/i, "").trim();
            if (!rest) return [];

            const cleaned = rest
                .replace(/\s*&\s*/g, " ")
                .replace(/\s*[\u2022•]\s*/g, " ")
                .replace(/\s*\n+\s*/g, " ")
                .trim();

            const splitByFailed = cleaned
                .split(/(?=Failed\b)/gi)
                .map((r) => r.trim().replace(/^[,;|.-\s]+/, ""))
                .filter((r) => r.length > 0);

            return splitByFailed.length ? splitByFailed : [cleaned];
        }
        return [];
    };

    const StatusStyled = ({ status }) => {
        if (status === "SUCCESS") {
            return (
                <p className="centeredp" style={{ color: "var(--positive)", fontWeight: "bold" }}>
                    SUCCESS
                </p>
            );
        }
        if (status === "FAIL") {
            return (
                <p className="centeredp" style={{ color: "var(--negative)", fontWeight: "bold" }}>
                    FAIL
                </p>
            );
        }
        return null;
    };

    const renderReasons = (reasons) => {
        if (!reasons.length) return null;

        return (
            <div style={{ marginTop: "6px" }}>
                {reasons.map((reason, idx) => (
                    <p key={idx}>
                        &nbsp;&nbsp;&nbsp;&nbsp;&#x2022;&nbsp;{reason}
                    </p>
                ))}
            </div>
        );
    };

    return (
        <>
            <section className="mainbody">
                <h2 className="responsiveheader">Current Fave</h2>
                {isReady ? (
                    <section className="centeredp specialrightsection glass-panel countdown-panel fade-in">
                        <p>
                            {daysleft} d: {hoursleft} h: {minutesleft} m: {secondsleft} s until
                            next fave
                        </p>
                    </section>
                ) : null}

                {error ? (
                    <p className="centeredp">{error}</p>
                ) : null}
                {isReady ? (
                    <StockDetails
                        stock={fave}
                        sectionClass={sectionClass}
                        highlightStyle={highlightStyle}
                    />
                ) : !error ? (
                    <p className="centeredp">Loading current fave...</p>
                ) : null}

                {isReady ? (
                    <div className="stockdiv fade-in" style={highlightStyle}>
                        <p className="centeredp">I.W.K. Status</p>
                        {latestFilterEntry && latestFilterEntry.detail ? (
                            (() => {
                                const chapter14 =
                                    latestFilterEntry.detail["Chapter 14 Analysis"] || "";
                                const chapter15 =
                                    latestFilterEntry.detail["Chapter 15 Analysis"] || "";

                                const ch14_status = extractStatus(chapter14);
                                const ch14_reasons = extractReasons(chapter14);

                                const ch15_status = extractStatus(chapter15);
                                const ch15_reasons = extractReasons(chapter15);

                                return (
                                    <div className="archivediv">
                                        <p className="centeredp">Chapter 14 Analysis:</p>
                                        <StatusStyled status={ch14_status} />
                                        {renderReasons(ch14_reasons)}

                                        <p className="centeredp" style={{ marginTop: "12px" }}>
                                            Chapter 15 Analysis:
                                        </p>
                                        <StatusStyled status={ch15_status} />
                                        {renderReasons(ch15_reasons)}
                                    </div>
                                );
                            })()
                        ) : (
                            <p className="centeredp">Not Enough Data Available.</p>
                        )}
                    </div>
                ) : null}

                <p className="centeredp">
                    Stock data last updated on: {dataDate || lastUpdate}
                </p>
            </section>
            <Footer />
        </>
    );
}

export default Fave;
